import { DateFormatType, formatNullableDate } from "~/common/utils/DateFormatter";
import { InfoBall } from "~/features/configurator/Linear/components/LinearInfo";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { useTranslation } from "react-i18next";

import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import type { QuoteDetailsMO } from "~/features/orders/common/components/v2/models";
import { QuoteIsBundleInfoModal } from "~/features/orders/quoteDetails/QuoteIsBundleInfoModal";
import { Param } from "../common/components/v2/Params";
import { Stack, Typography } from "~/common/mui";
import Tag from "~/common/components/Tag/Tag";
import { DetailsRow, QuoteDetailsVisibleData } from "../common/components/v2/OrderAndQuoteSharedStyles";

interface OrderSummaryProps {
  quote: QuoteDetailsMO;
}

export const QuoteDetailsSummaryV2 = ({ quote }: OrderSummaryProps) => {
  const { t } = useTranslation();
  const { val: showBundlePopup, setTrue: openBundlePopup, setFalse: closeBundlePopup } = useBooleanState(false);
  const canSeeComments = userHasPermission(Permission.ADD_VIEW_HEADER_LEVEL_COMMENTS);
  const showComment = quote.comment && canSeeComments;

  return (
    <>
      <QuoteDetailsVisibleData>
        <DetailsRow>
          <Param label={t("quoteDetailsPage.summary.quoteNumber")} value={quote.soNumber}></Param>
          <Param label={t("quoteDetailsPage.summary.customerRefNo")} value={quote.poNumber}></Param>
          <Param
            label={t("quoteDetailsPage.summary.quoteStatus")}
            value={
              <Tag variant={quote.status === "Q1" ? "success" : "default"}>
                {t(`codes.quoteStatus.${quote.status}`)}
              </Tag>
            }
          ></Param>
          <Param
            label={t("quoteDetailsPage.summary.dateEntered")}
            value={formatNullableDate(quote.datePlaced, DateFormatType.DATE)}
          ></Param>
          <Param
            label={t("quoteDetailsPage.summary.dateExpired")}
            value={formatNullableDate(quote.dateExpired, DateFormatType.DATE)}
          ></Param>
          <Param label={t("quoteDetailsPage.summary.accNo")} value={quote.accNo}></Param>
          <Param
            label={t("quoteDetailsPage.summary.quoteType")}
            value={
              <Stack flexDirection={"row"} gap={0.5}>
                <Typography variant="bodyLarge" color="text.secondary">
                  {t(quote.quoteType)}
                </Typography>
                {quote.isBundledQuote && <InfoBall onClick={openBundlePopup} />}
                {showBundlePopup && <QuoteIsBundleInfoModal onClose={closeBundlePopup} />}
              </Stack>
            }
          ></Param>
          <Param label={t("quoteDetailsPage.summary.orderAttainment")} value={quote.orderAttainment}></Param>
          <Param label={t("quoteDetailsPage.summary.orderFullfilment")} value={quote.orderFulfilment}></Param>
        </DetailsRow>

        {showComment && (
          <Stack>
            <Param label={t("quoteDetails.page.comment.title")} value={quote.comment}></Param>
          </Stack>
        )}
      </QuoteDetailsVisibleData>
    </>
  );
};
